// Word import in the browser (File ▸ Import ▸ Word Document… on the Mac): a .docx read in this browser, made into a new
// project by `@writing-suite/core/import`'s `importWordDocument` (headings make the binder; comments, links, footnotes
// and pictures come too) and put on the relay as any new project is (`createProject` in planNewProject.ts); its pictures
// go up as attachment snapshots, as a Scrivener import's do (`uploadAttachments`). Nothing of the file leaves the
// browser but the sealed documents and pictures.
import * as A from '@automerge/automerge';
import { unzipSync } from 'fflate';
import { arrangementTree, readProject, type ArrangementTreeNode } from '@writing-suite/core';
import {
  buildWordOutline, importWordDocument, isLegacyWord, readWordDocument, WordImportError,
  type ImportedAttachment, type WordFiles, type WordImportReport,
} from '@writing-suite/core/import';
import type { NewProjectDoc } from './planNewProject.ts';

/** A Word document chosen to import: its bytes, and what a first reading found, for the dialog. */
export interface WordFile {
  /** The file's name without `.docx`: the project's title when the document gives none. */
  filename: string;
  bytes: Uint8Array;
  /** The title the import will give the project. */
  title: string;
  /** "12 chapters, 3 parts" or the like, and the document's words. */
  summary: string;
  words: number;
}

export interface ImportedWordProject {
  built: { projectId: string; type: 'manuscript'; docs: NewProjectDoc[]; opening: string | null };
  report: WordImportReport;
  attachments: ImportedAttachment[];
}

/** The .docx's parts, or null when the bytes aren't a zip. */
function partsOf(bytes: Uint8Array): WordFiles | null {
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(bytes);
  } catch {
    return null;
  }
  return (path) => entries[path] ?? null;
}

/** Reads a chosen file far enough to name it: throws the Mac's message for a .doc or anything that isn't Word's. */
export function wordFileFrom(name: string, bytes: Uint8Array): WordFile {
  if (isLegacyWord(bytes)) throw new WordImportError('legacyWordFormat');
  const files = partsOf(bytes);
  if (!files) throw new WordImportError('notAWordDocument');
  const filename = name.replace(/\.docx$/i, '');
  const outline = buildWordOutline(readWordDocument(files), filename);
  const structure = outline.summary.find((s) => s.startsWith('Structure: '));
  const summary = structure ? structure.slice('Structure: '.length).replace(/ from .*$/, '').replace(/;.*$/, '') : 'One document';
  return { filename, bytes, title: outline.title, summary, words: outline.sourceWords };
}

/** Imports the document as a new project: the documents to put on the relay, the pictures to upload, and the report. */
export function importWordFile(file: WordFile, o: { author: string; locale: string; title?: string }): ImportedWordProject {
  const result = importWordDocument(file.bytes, partsOf(file.bytes), { filename: file.filename, author: o.author, locale: o.locale, title: o.title });
  const project = readProject(result.docs.get('project')!);
  const manuscript = project.arrangements.find((a) => a.role === 'manuscript');
  return {
    built: {
      projectId: result.projectID,
      type: result.type,
      docs: [...result.docs].map(([name, doc]) => ({ name, bytes: A.save(doc) })),
      opening: manuscript ? firstDocument(arrangementTree(manuscript)) : null,
    },
    report: result.report,
    attachments: result.attachments,
  };
}

function firstDocument(nodes: ArrangementTreeNode[]): string | null {
  for (const n of nodes) {
    if (n.node.fragmentID && !n.node.isFolder) return n.node.id;
    const inner = firstDocument(n.children);
    if (inner) return inner;
  }
  return null;
}
